import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Cake, Building2, PartyPopper, Utensils, Wine, Check, Loader2 } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { Reveal } from "../hooks/useReveal";
import { useToast } from "../hooks/use-toast";
import { BRAND, IMAGES } from "../mock";
import { DatePickerField } from "../components/DateTimeFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  { icon: Cake, title: "Birthdays & Milestones", desc: "Make your celebration unforgettable with dedicated service, sparklers, and custom presentations." },
  { icon: Building2, title: "Corporate Gatherings", desc: "Impress clients and reward teams in an upscale, distraction-free setting with full-service catering." },
  { icon: PartyPopper, title: "Private Parties", desc: "Full or partial venue buyouts with live DJs, bottle service, and a night your guests will remember." },
  { icon: Wine, title: "Bottle Service Events", desc: "Premium tables, curated bottle packages, and VIP treatment for you and your guests." },
];

const HIGHLIGHTS = [
  "Customizable food & beverage packages",
  "Dedicated event coordinator",
  "Live DJ & entertainment options",
  "Full & partial venue buyouts",
  "VIP bottle service & sparkler presentations",
  "Upscale ambiance in the heart of Atlanta",
];

export default function PrivateEvents() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", eventType: "Birthday", date: "", guests: "20-50", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/events`, form);
      setSent(true);
      toast({ title: "Event Inquiry Received", description: `Thank you, ${form.name}. Our events team will contact you within 24 hours.` });
    } catch (err) {
      toast({ title: "Submission Failed", description: `Please call us at ${BRAND.phone}.`, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-nala-charcoal border border-nala-gold/25 text-nala-cream px-4 py-3 focus:outline-none focus:border-nala-gold transition-colors placeholder:text-nala-cream-muted/50";

  return (
    <div className="pt-28 pb-24 bg-nala-black min-h-screen">
      {/* Banner */}
      <div className="relative h-72 md:h-96 overflow-hidden mb-16">
        <img src={IMAGES.ckGroup} alt="Private events at Nala" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-nala-black via-nala-black/55 to-nala-black/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <p className="text-nala-gold tracking-luxe uppercase text-sm mb-3">Host With Us</p>
          <h1 className="font-display text-5xl md:text-7xl text-gold-gradient">Private Events</h1>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        {/* Intro */}
        <Reveal className="text-center max-w-3xl mx-auto mb-20">
          <p className="font-serif-lux text-xl md:text-2xl text-nala-cream-muted leading-relaxed">
            Looking for the perfect venue? Whether it's a corporate gathering, birthday celebration,
            or private party, Nala offers an upscale setting, exceptional service, and customizable
            packages to make your event truly special.
          </p>
        </Reveal>

        {/* Event types */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-28">
          {EVENT_TYPES.map((e, i) => (
            <Reveal key={e.title} delay={i * 80} className="border border-nala-gold/15 bg-nala-charcoal/50 p-8 hover:border-nala-gold/40 hover:-translate-y-1 transition-all duration-500">
              <div className="w-12 h-12 rounded-full border border-nala-gold/50 flex items-center justify-center text-nala-gold mb-5">
                <e.icon size={22} />
              </div>
              <h3 className="font-display text-xl text-nala-cream mb-3">{e.title}</h3>
              <p className="text-nala-cream-muted text-sm font-serif-lux text-base leading-relaxed">{e.desc}</p>
            </Reveal>
          ))}
        </div>

        {/* Highlights split */}
        <div className="grid lg:grid-cols-2 gap-14 items-center mb-28">
          <Reveal className="grid grid-cols-2 gap-4">
            <img src={IMAGES.ckSuperior1} alt="Signature cocktail" className="w-full h-80 object-cover border border-nala-gold/20" />
            <img src={IMAGES.lamb} alt="Lamb lollipops" className="w-full h-80 object-cover mt-10 border border-nala-gold/20" />
          </Reveal>
          <Reveal delay={120}>
            <p className="text-nala-gold tracking-luxe uppercase text-sm mb-4">Why Nala</p>
            <h2 className="font-display text-4xl text-nala-cream mb-8 leading-tight">An Occasion <span className="text-gold-gradient">Worthy of You</span></h2>
            <ul className="space-y-4">
              {HIGHLIGHTS.map((h) => (
                <li key={h} className="flex items-center gap-4 text-nala-cream font-serif-lux text-lg">
                  <span className="w-6 h-6 rounded-full bg-nala-gold/15 border border-nala-gold flex items-center justify-center text-nala-gold shrink-0">
                    <Check size={13} />
                  </span>
                  {h}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* Catering */}
        <div className="grid lg:grid-cols-2 gap-14 items-center mb-28">
          <Reveal className="order-2 lg:order-1">
            <p className="text-nala-gold tracking-luxe uppercase text-sm mb-4">Catering To-Go</p>
            <h2 className="font-display text-4xl text-nala-cream mb-6 leading-tight">Bring Nala <span className="text-gold-gradient">to Your Table</span></h2>
            <p className="font-serif-lux text-xl text-nala-cream-muted leading-relaxed mb-6">
              From intimate gatherings to large-scale functions, we provide expertly crafted dishes
              and beverages tailored to your needs. Let our team handle the details while you enjoy
              the celebration.
            </p>
            <div className="flex items-center gap-3 text-nala-cream mb-8">
              <Utensils className="text-nala-gold" size={20} />
              <span className="font-serif-lux text-lg">Custom menus · Delivery · On-site service</span>
            </div>
            <Link to="/contact" className="btn-gold"><span>Request Catering</span></Link>
          </Reveal>
          <Reveal delay={120} className="order-1 lg:order-2 border border-nala-gold/20 overflow-hidden">
            <img src={IMAGES.lobster2} alt="Catering" className="w-full h-96 object-cover" />
          </Reveal>
        </div>

        {/* Inquiry Form */}
        <div id="inquire" className="mb-24">
          <SectionHeading eyebrow="Plan With Us" title="Event Inquiry" subtitle="Tell us about your celebration and our events team will craft a custom experience." />
          <div className="grid lg:grid-cols-2 gap-12 mt-16 items-start">
            <Reveal className="relative overflow-hidden border border-nala-gold/25 min-h-[420px]">
              <img src={IMAGES.ckGroup} alt="Celebrate at Nala" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-nala-black via-nala-black/40 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <h3 className="font-display text-3xl text-nala-cream mb-3">Your Night, Elevated</h3>
                <p className="font-serif-lux text-lg text-nala-cream-muted">Prefer to talk? Call us at <a href={BRAND.phoneHref} className="text-nala-gold">{BRAND.phone}</a></p>
              </div>
            </Reveal>

            <Reveal delay={120}>
              {sent ? (
                <div className="border border-nala-gold/30 bg-nala-charcoal p-10 text-center">
                  <div className="w-16 h-16 rounded-full bg-nala-gold/15 border border-nala-gold flex items-center justify-center mx-auto mb-6 text-nala-gold">
                    <Check size={30} />
                  </div>
                  <h3 className="font-display text-3xl text-nala-cream mb-4">Inquiry Received</h3>
                  <p className="font-serif-lux text-lg text-nala-cream-muted mb-8">
                    Thank you, {form.name}. Our events team will reach out within 24 hours to plan your celebration.
                  </p>
                  <button onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", eventType: "Birthday", date: "", guests: "20-50", message: "" }); }} className="btn-outline-gold">
                    <span>Submit Another</span>
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="border border-nala-gold/20 bg-nala-charcoal/50 p-8 md:p-10">
                  <div className="space-y-4">
                    <input required placeholder="Full Name *" value={form.name} onChange={update("name")} className={inputClass} />
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input required type="email" placeholder="Email *" value={form.email} onChange={update("email")} className={inputClass} />
                      <input required placeholder="Phone *" value={form.phone} onChange={update("phone")} className={inputClass} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <select value={form.eventType} onChange={update("eventType")} className={inputClass}>
                        <option>Birthday</option><option>Corporate Gathering</option><option>Private Party</option>
                        <option>Bottle Service Event</option><option>Day Party</option><option>Other</option>
                      </select>
                      <select value={form.guests} onChange={update("guests")} className={inputClass}>
                        <option value="10-20">10 – 20 guests</option>
                        <option value="20-50">20 – 50 guests</option>
                        <option value="50-100">50 – 100 guests</option>
                        <option value="100+">100+ guests</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-nala-cream-muted text-xs uppercase tracking-wide-lux mb-2 block">Preferred Date</label>
                      <DatePickerField
                        value={form.date}
                        onChange={(v) => setForm((f) => ({ ...f, date: v }))}
                        placeholder="Select a date"
                        triggerClassName="bg-nala-charcoal border-nala-gold/25 text-nala-cream focus:border-nala-gold"
                      />
                    </div>
                    <textarea rows={4} placeholder="Tell us about your event..." value={form.message} onChange={update("message")} className={inputClass} />
                  </div>
                  <button type="submit" disabled={loading} className="btn-gold w-full mt-6 disabled:opacity-70"><span className="flex items-center gap-2">{loading && <Loader2 size={15} className="animate-spin" />}{loading ? "Submitting..." : "Submit Inquiry"}</span></button>
                  <p className="text-nala-cream-muted/60 text-xs text-center mt-4 tracking-wide-lux">Typical reply within 24 hours.</p>
                </form>
              )}
            </Reveal>
          </div>
        </div>

        {/* CTA */}
        <Reveal className="relative overflow-hidden border border-nala-gold/25">
          <img src={IMAGES.ckPurpleDill} alt="Book event" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-nala-black/85" />
          <div className="relative text-center py-16 px-6">
            <h2 className="font-display text-3xl md:text-4xl text-nala-cream mb-5">Let's Plan Something Unforgettable</h2>
            <p className="font-serif-lux text-lg text-nala-cream-muted mb-8 max-w-xl mx-auto">
              Reach out to our events team and we'll craft a custom experience just for you.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#inquire" className="btn-gold"><span>Start Your Inquiry</span></a>
              <a href={BRAND.phoneHref} className="btn-outline-gold"><span>{BRAND.phone}</span></a>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
